/**
 * Shared Details step. Wraps `EncounterDetailsSection` and reads/writes
 * its fields directly from the typed form's `useForm` instance, so the
 * typed forms no longer hand-wire 30 lines of `setFieldValue` callbacks
 * apiece.
 *
 * @agentic connections
 */

import type { UseFormReturnType } from "@mantine/form";

import {
  formatDateTimeForInput,
  formatInputForDateTime,
} from "@benchmark/ui/utils";
import type { EnhancedSelectOption } from "@/types/ui/component-props";

import { EncounterDetailsSection } from "../sections/EncounterDetailsSection";

/**
 * Form shapes that own the encounter-details fields. Every typed encounter
 * form data shape includes these fields via `EncounterFormDataBase` (and
 * `"encounter-billable"` on intake / legacy shapes).
 *
 * @agentic connections
 */
type DetailsCapableFormData = {
  period?: { start?: string; end?: string };
  "encounter-method"?: string;
  "encounter-inout"?: string;
  "encounter-billable"?: string;
};

/**
 * Props for {@link EncounterDetailsStep}.
 *
 * @agentic connections
 */
export type EncounterDetailsStepProps<T extends DetailsCapableFormData> = {
  form: UseFormReturnType<T>;
  methodOptions: EnhancedSelectOption[];
  directionOptions: EnhancedSelectOption[];
  billableOptions: EnhancedSelectOption[];
  requiresBillable: boolean;
  readOnly?: boolean;
};

/**
 * Renders the Details step of an encounter form: timing, method,
 * direction, and (when intake) billable status.
 *
 * @agentic connections
 */
export const EncounterDetailsStep = <T extends DetailsCapableFormData>({
  form,
  methodOptions,
  directionOptions,
  billableOptions,
  requiresBillable,
  readOnly = false,
}: EncounterDetailsStepProps<T>) => {
  const periodStart = form.values.period?.start ?? "";
  const periodEnd = form.values.period?.end ?? "";
  const encounterMethod = form.values["encounter-method"] ?? "";
  const encounterDirection = form.values["encounter-inout"] ?? "";
  const encounterBillable = form.values["encounter-billable"] ?? "";

  return (
    <EncounterDetailsSection
      periodStart={periodStart}
      periodEnd={periodEnd}
      encounterMethod={encounterMethod}
      encounterDirection={encounterDirection}
      encounterBillable={encounterBillable}
      periodStartError={form.errors["period.start"] as string | undefined}
      periodEndError={form.errors["period.end"] as string | undefined}
      encounterMethodError={
        form.errors["encounter-method"] as string | undefined
      }
      encounterDirectionError={
        form.errors["encounter-inout"] as string | undefined
      }
      encounterBillableError={
        form.errors["encounter-billable"] as string | undefined
      }
      methodOptions={methodOptions}
      directionOptions={directionOptions}
      billableOptions={billableOptions}
      requiresBillable={requiresBillable}
      readOnly={readOnly}
      formatDateTimeForInput={formatDateTimeForInput}
      formatInputForDateTime={formatInputForDateTime}
      onPeriodStartChange={(value) =>
        form.setFieldValue("period.start" as never, value as never)
      }
      onPeriodEndChange={(value) =>
        form.setFieldValue("period.end" as never, value as never)
      }
      onMethodChange={(value) =>
        form.setFieldValue("encounter-method", value as never)
      }
      onDirectionChange={(value) =>
        form.setFieldValue("encounter-inout", value as never)
      }
      onBillableChange={(value) =>
        requiresBillable
          ? form.setFieldValue("encounter-billable", value as never)
          : undefined
      }
    />
  );
};
